// Mastering engine — live preview chain + offline full-length WAV render.
// Independent of the main studio engine (own AudioContext).
import { encodeWAV } from "./wav";

const dbToGain = (db) => Math.pow(10, db / 20);

// Soft-clip saturation curve (adds harmonics = perceived "warmth/fullness").
function makeSatCurve(amount) {
  const k = (amount / 100) * 4; // gentle drive — subtle by default, warm when pushed
  const n = 2048;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * (1 + k)) / Math.tanh(1 + k || 1);
  }
  return curve;
}

// Presets → user controls. bass/mid/air = dB (-10..+10); warmth/punch/loudness = 0..100.
// Tuned clarity-first: clean by default, loudness/punch stay audible, no instant distortion.
export const MASTER_PRESETS = {
  balanced: { label: "Balanced", bass: 1, mid: 0, air: 2, warmth: 10, punch: 25, loudness: 42 },
  warm: { label: "Warm", bass: 3, mid: -1, air: -1, warmth: 35, punch: 22, loudness: 40 },
  club: { label: "Loud (Club)", bass: 2.5, mid: -0.5, air: 2.5, warmth: 20, punch: 55, loudness: 70 },
  streaming: { label: "Streaming-ready", bass: 1.5, mid: 0, air: 2, warmth: 12, punch: 35, loudness: 48 },
  trap: { label: "Hip-Hop / Trap", bass: 5, mid: -2, air: 3, warmth: 22, punch: 48, loudness: 62 },
};

export const DEFAULT_CONTROLS = { bass: 1, mid: 0, air: 2, warmth: 10, punch: 25, loudness: 42 };

// Build the mastering signal chain in any context (online preview or offline render).
function buildChain(ctx, c) {
  const input = ctx.createGain();
  input.gain.value = 0.63; // ~-4 dB headroom so EQ + saturation don't clip before the limiter

  const low = ctx.createBiquadFilter();
  low.type = "lowshelf";
  low.frequency.value = 110;

  const mid = ctx.createBiquadFilter();
  mid.type = "peaking";
  mid.frequency.value = 1200;
  mid.Q.value = 0.7;

  const high = ctx.createBiquadFilter();
  high.type = "highshelf";
  high.frequency.value = 8000;

  // saturation stage
  const preSat = ctx.createGain();
  const sat = ctx.createWaveShaper();
  sat.oversample = "2x";
  const postSat = ctx.createGain();

  const comp = ctx.createDynamicsCompressor();
  comp.knee.value = 8;
  comp.release.value = 0.2;
  const makeup = ctx.createGain();

  const preGain = ctx.createGain();

  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -1.0;
  limiter.ratio.value = 20;
  limiter.knee.value = 0;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.06;

  const ceiling = ctx.createGain();
  ceiling.gain.value = 0.95; // ~-0.4 dBFS headroom

  input.connect(low);
  low.connect(mid);
  mid.connect(high);
  high.connect(preSat);
  preSat.connect(sat);
  sat.connect(postSat);
  postSat.connect(comp);
  comp.connect(makeup);
  makeup.connect(preGain);
  preGain.connect(limiter);
  limiter.connect(ceiling);

  const nodes = { input, low, mid, high, preSat, sat, postSat, comp, makeup, preGain, limiter, ceiling };
  applyToNodes(nodes, c);
  return { input, output: ceiling, nodes };
}

function applyToNodes(n, c) {
  n.low.gain.value = c.bass;
  n.mid.gain.value = c.mid;
  n.high.gain.value = c.air;
  // gentle analog-style saturation — subtle by default so finished tracks stay clean
  const w = c.warmth / 100;
  n.preSat.gain.value = 1 + w * 0.9;
  n.sat.curve = makeSatCurve(c.warmth);
  n.postSat.gain.value = 1 / (1 + w * 0.9);
  // glue compressor: musical, not crushing — slower attack keeps transients & clarity
  const p = c.punch / 100;
  n.comp.threshold.value = -6 - p * 16;   // -6..-22 dB
  n.comp.ratio.value = 1.5 + p * 3.5;      // 1.5..5:1
  n.comp.attack.value = 0.02 - p * 0.014;  // 20ms..6ms
  n.comp.release.value = 0.18;
  n.makeup.gain.value = dbToGain(p * 1.5); // light makeup so Punch stays audible
  // loudness pushes into the limiter — a clear, audible sweep from clean to loud
  n.preGain.gain.value = dbToGain((c.loudness / 100) * 8);
}

export class MasteringEngine {
  constructor() {
    this.ctx = null;
    this.buffer = null;
    this.controls = { ...DEFAULT_CONTROLS };
    this.bypass = false; // true = hear original (A), false = hear mastered (B)
    this.source = null;
    this.isPlaying = false;
    this._startTime = 0;
    this._offset = 0;
    this.onended = null;
  }

  _ensureCtx() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.dryGain = this.ctx.createGain();
      this.wetGain = this.ctx.createGain();
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.dryGain.connect(this.analyser);
      this.wetGain.connect(this.analyser);
      this.analyser.connect(this.ctx.destination);
      this._applyMix();
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  async loadFile(file) {
    this._ensureCtx();
    const arr = await file.arrayBuffer();
    this.buffer = await this.ctx.decodeAudioData(arr);
    this.fileName = file.name.replace(/\.[^.]+$/, "");
    this._offset = 0;
    return { duration: this.buffer.duration, name: this.fileName };
  }

  _applyMix() {
    if (!this.dryGain) return;
    this.dryGain.gain.value = this.bypass ? 1 : 0;
    this.wetGain.gain.value = this.bypass ? 0 : 1;
  }

  setBypass(on) {
    this.bypass = on;
    this._applyMix();
  }

  setControls(c) {
    this.controls = { ...this.controls, ...c };
    if (this._liveNodes) applyToNodes(this._liveNodes, this.controls);
  }

  applyPreset(key) {
    const p = MASTER_PRESETS[key];
    if (!p) return;
    const { label, ...vals } = p;
    this.setControls(vals);
    return { ...vals };
  }

  play() {
    if (!this.buffer) return;
    this._ensureCtx();
    this.stop(true);
    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.loop = true; // loop the preview so tweaks are continuous
    const chain = buildChain(this.ctx, this.controls);
    this._liveNodes = chain.nodes;
    src.connect(this.dryGain); // original path
    src.connect(chain.input); // mastered path
    chain.output.connect(this.wetGain);
    this._applyMix();
    const off = this._offset % this.buffer.duration;
    src.start(0, off);
    this._startTime = this.ctx.currentTime - off;
    this.source = src;
    this.isPlaying = true;
    src.onended = () => {
      if (this.source === src) {
        this.isPlaying = false;
        this._offset = 0;
        if (this.onended) this.onended();
      }
    };
  }

  pause() {
    if (!this.isPlaying) return;
    this._offset = this.position;
    this.stop(true);
  }

  stop(keepOffset) {
    if (this.source) {
      try {
        this.source.onended = null;
        this.source.stop();
      } catch (e) {}
      this.source = null;
    }
    this.isPlaying = false;
    if (!keepOffset) this._offset = 0;
  }

  seek(sec) {
    this._offset = Math.max(0, Math.min(sec, this.duration));
    if (this.isPlaying) this.play();
  }

  get duration() {
    return this.buffer ? this.buffer.duration : 0;
  }

  get position() {
    if (!this.isPlaying) return this._offset;
    const elapsed = this.ctx.currentTime - this._startTime;
    return this.duration ? elapsed % this.duration : 0; // wraps for the looping preview
  }

  // Approximate short-term loudness (LUFS-ish, no K-weighting/gating).
  loudness() {
    if (!this.analyser) return -Infinity;
    const buf = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const ms = sum / buf.length;
    if (ms <= 1e-9) return -70;
    return -0.691 + 10 * Math.log10(ms);
  }

  // Render the whole song offline (fast) with the current mastering chain.
  async renderWav() {
    if (!this.buffer) return null;
    const off = new OfflineAudioContext(2, this.buffer.length, this.buffer.sampleRate);
    const src = off.createBufferSource();
    src.buffer = this.buffer;
    const chain = buildChain(off, this.controls);
    src.connect(chain.input);
    chain.output.connect(off.destination);
    src.start();
    const rendered = await off.startRendering();
    const l = rendered.getChannelData(0);
    const r = rendered.numberOfChannels > 1 ? rendered.getChannelData(1) : l;
    return encodeWAV([l, r], rendered.sampleRate);
  }
}

export const mastering = new MasteringEngine();
